// Story 1.16 — TanStack Query mutation hook for POST /api/v1/wallet/topup-intent.
//
// Generates a per-submit idempotency key (NOT per modal mount — retries
// from the same submit reuse the key so the middleware caches the
// response correctly). The hook returns the hosted-checkout handoff the
// modal needs: either `completed=true` (mock provider credited the
// balance immediately) or a `checkout_url` to open in a new tab.
//
// Top-up is 1:1 (product decision 2026-08-03): no fee_breakdown in the
// response — €100 in nets €100 of balance. Platform fees live on the
// sale side.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type TopupIntentRequest = {
  amount_cents: number;
};

export type TopupIntentResponse = {
  /** Hosted payment page to open in a new tab. Null when
   *  `completed=true` (mock short-circuit: balance credited server-side
   *  immediately — no checkout step exists). */
  checkout_url: string | null;
  provider_ref: string;
  completed: boolean;
};

export type TopUpError = {
  code: string;
  message: string;
  field?: string;
};

async function postTopupIntent(
  amount_cents: number,
  idempotencyKey: string,
): Promise<TopupIntentResponse> {
  const response = await fetch("/api/v1/wallet/topup-intent", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({ amount_cents }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: TopUpError = {
      code: "TOPUP_INTENT_FAILED",
      message: `Top-up failed (HTTP ${response.status})`,
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  return (await response.json()) as TopupIntentResponse;
}

export function useTopUpIntent() {
  const queryClient = useQueryClient();
  return useMutation<
    TopupIntentResponse,
    TopUpError,
    { amount_cents: number; idempotencyKey: string }
  >({
    mutationFn: ({ amount_cents, idempotencyKey }) =>
      postTopupIntent(amount_cents, idempotencyKey),
    onSuccess: () => {
      // completed=true means the balance was already credited server-side;
      // completed=false means the credit lands via the provider webhook
      // after the hosted checkout finishes. Either way, invalidating here
      // makes the BalanceDisplay refetch as soon as it matters.
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    },
  });
}
